import React, { useEffect } from 'react';
import SelectIcon from '../images/Icon20_Pointer.svg';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';
import {
  setActiveObject,
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/fabric_overlay';
import {
  addControlPointsToLine,
  disableSelectionControls,
  overrideContainsPoint,
  removeControlPoints,
  resetContainsPoint,
  resetObjectStyle,
  selectObjectStyle,
} from '../helpers/fabricHelpers';
import { __, setTableParams } from '../../../core/utils';

const Select = ({
  fabric_overlay: { activeTool, viewer, fabricOverlay },
  plan,
  user_id,
}) => {
  const _handleReadTask = (taskID) => {
    setTableParams('all_tasks', {
      clicked_row: taskID,
    });
  };

  useEffect(() => {
    if (!fabricOverlay || activeTool !== 'select') return;

    const canvas = fabricOverlay.fabricCanvas();
    canvas.defaultCursor = 'default';
    canvas.hoverCursor = 'pointer';
    canvas.moveCursor = 'grabbing';

    canvas.selection = true;

    setCursorTooltip(__('canvas.tooltip.select_markups'));

    let task_modified = false;

    canvas.forEachObject((obj) => {
      if (obj.objectType == 'task') {
        obj.hasControls = false;
        obj.selectable = true;
        obj.hasBorders = false;
        obj.evented = true;
        obj.lockMovementX = false;
        obj.lockMovementY = false;

        if (user_id != obj.userID) {
          obj.lockMovementX = true;
          obj.lockMovementY = true;
        }
      } else if (
        _.includes(['line', 'freehand', 'polygon', 'cloud'], obj.objectType)
      ) {
        obj.hasControls = false;
        obj.selectable = true;
        obj.hasBorders = false;
        obj.evented = true;

        if (obj.objectType === 'line') {
          obj.perPixelTargetFind = false;

          overrideContainsPoint(obj, canvas);
        }
      } else {
        obj.selectable = true;
        obj.evented = true;
        obj.hasControls = true;
        obj.hasBorders = true;
        obj.strokeUniform = true;
      }

      obj.setCoords();
    });

    canvas.requestRenderAll();

    const onPointerDown = (e) => {
      if (e.pointerType === 'mouse') {
        return viewer.setMouseNavEnabled(false);
      }

      if (e.pointerType === 'touch' && e.touches && e.touches.length != 2) {
        return viewer.setMouseNavEnabled(false);
      }

      // const target = canvas.findTarget(e);
      // if (target) {
      //   viewer.setMouseNavEnabled(false);
      // } else {
      //   viewer.setMouseNavEnabled(true);
      //   if (canvas.getActiveObject()) {
      //     canvas.discardActiveObject();
      //     canvas.requestRenderAll();
      //   }
      // }
    };

    const mouseUp = (e) => {
      viewer.setMouseNavEnabled(true);

      const obj = e.target;

      if (obj?.objectType === 'line') {
        removeControlPoints(canvas);
        addControlPointsToLine(obj, canvas, viewer, plan);
      }

      if (obj?.objectType === 'task') {
        if (!task_modified) {
          _handleReadTask(obj.taskID);
        }

        task_modified = false;

        resetObjectStyle(obj);
        canvas.discardActiveObject();

        canvas.requestRenderAll();
      }
    };

    const mouseDown = (e) => {
      const obj = e.target;

      if (!obj) {
        canvas.forEachObject((obj) => {
          if (obj.objectType === 'task') {
            obj.selectable = false;
          }
        });
      }

      if (obj?.objectType === 'line') {
        removeControlPoints(canvas);
      }
    };

    const mouseMove = (e) => {
      viewer.setMouseNavEnabled(false);
    };

    const handleSelectionCreated = (e) => {
      const target = e.target;

      if (target.type != 'activeSelection') {
        const obj = e.selected?.[0];

        if (obj?.objectType === 'line') {
          removeControlPoints(canvas);
          resetContainsPoint(obj);
        }

        selectObjectStyle(obj);

        if (obj?.objectType !== 'task') {
          setActiveObject(obj);
          setCursorTooltip(__('canvas.tooltip.delete_markups'));
        }
      } else {
        disableSelectionControls(target);

        setActiveObject(e.selected);

        setCursorTooltip(__('canvas.tooltip.delete_markups'));
      }
    };

    const handleSelectionUpdated = (e) => {
      const target = e.target;

      if (target.type != 'activeSelection') {
        const obj_selected = e.selected?.[0];
        const obj_deselected = e.deselected?.[0];

        if (obj_selected?.objectType === 'line') {
          //removeControlPoints(canvas);
          resetContainsPoint(obj_selected);
        }

        if (obj_deselected?.objectType === 'line') {
          overrideContainsPoint(obj_deselected, canvas);
        }

        if (
          _.includes(['line', 'controlpoint'], obj_deselected?.objectType) &&
          !_.includes(['line', 'controlpoint'], obj_selected?.objectType)
        ) {
          removeControlPoints(canvas);
        }

        selectObjectStyle(obj_selected);

        if (obj_selected?.objectType !== 'task') setActiveObject(obj_selected);

        resetObjectStyle(obj_deselected);
      } else {
        target.getObjects().forEach((obj) => {
          resetObjectStyle(obj);
        });

        disableSelectionControls(target);

        setActiveObject(e.selected);
      }
    };

    const handleSelectionCleared = (e) => {
      canvas.forEachObject((obj) => {
        if (obj.objectType === 'task') {
          obj.selectable = true;
        }
      });

      const obj = e.deselected?.[0];
      if (_.includes(['line', 'controlpoint'], obj?.objectType)) {
        removeControlPoints(canvas);
        overrideContainsPoint(obj, canvas);
      }
      resetObjectStyle(obj);

      setActiveObject(null);

      setCursorTooltip(__('canvas.tooltip.select_markups'));
    };

    const handleObjectModified = (e) => {
      const obj = e.target;

      if (obj.objectType === 'task') {
        task_modified = true;
      }

      if (obj.objectType !== 'line') return;

      const dx = obj.left - obj.startLeft;
      const dy = obj.top - obj.startTop;

      obj.set({
        x1: obj.x1 + dx,
        y1: obj.y1 + dy,
        x2: obj.x2 + dx,
        y2: obj.y2 + dy,
        startLeft: obj.left,
        startTop: obj.top,
      });

      obj.setCoords();

      addControlPointsToLine(obj, canvas, viewer, plan);
    };

    canvas.on('selection:created', handleSelectionCreated);
    canvas.on('selection:updated', handleSelectionUpdated);
    canvas.on('mouse:down', mouseDown);
    //canvas.on('mouse:move', mouseMove);

    canvas.on('mouse:up', mouseUp);
    canvas.on('selection:cleared', handleSelectionCleared);
    canvas.on('object:modified', handleObjectModified);

    canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);

    return () => {
      if (canvas.getActiveObject()) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }

      canvas.selection = false;

      canvas.off('selection:created', handleSelectionCreated);
      canvas.off('selection:cleared', handleSelectionCleared);
      canvas.off('selection:updated', handleSelectionUpdated);
      canvas.off('mouse:down', mouseDown);
      //canvas.off('mouse:move', mouseMove);
      canvas.off('mouse:up', mouseUp);
      canvas.off('object:modified', handleObjectModified);
      canvas.upperCanvasEl.removeEventListener('pointerdown', onPointerDown);
      removeControlPoints(canvas);
    };
  }, [fabricOverlay, activeTool]);

  const handleClick = () => {
    updateTool({ tool: 'select' });
  };

  return (
    <Button
      id='select'
      label={__('canvas.toolbar.select')}
      icon={SelectIcon}
      active={activeTool === 'select'}
      onClick={handleClick}
      extraClass={['withSpacer']}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    fabric_overlay: state.fabric_overlay,
    user_id: state.auth.id,
  };
};

export default connect(mapStateToProps, null)(Select);
